import { memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import Close from '@root/lib/components/Icons/Close';
import cn from 'classnames';
import './Toast.scss';

/**
 * @param {{
 *   id: number;
 *   content: string;
 *   error: boolean;
 *   onDismiss: (id: number) => void;
 * }} props
 */
const Toast = ({
  id,
  content,
  error,
  onDismiss,
}) => {
  const timeoutRef = useRef();
  const [ animationProps, setAnimationProps ] = useSpring(() => ({
    opacity: 0,
    transform: 'translateX(100%)',
  }));

  /**
   * Stops the auto dismissal of the toast
   */
  const stopAutoDismissal = useCallback(() => {
    clearTimeout(timeoutRef.current);
  }, []);

  /**
   * Clears the timeout and calls the onDismiss
   */
  const onDismissProxy = useCallback(() => {
    stopAutoDismissal();
    setAnimationProps({opacity: 0, transform: 'transform(-100%)', onRest: () => {
      onDismiss(id);
    }});
  }, [onDismiss, setAnimationProps, id, stopAutoDismissal]);

  /**
   * Resumes/starts auto dismissal of the toast
   */
  const resumeAutoDismissal = useCallback(() => {
    timeoutRef.current = setTimeout(onDismissProxy, 5000);
  }, [onDismissProxy]);

  // Dismissing the toast after certain time on screen
  useEffect(() => {
    resumeAutoDismissal();
    return () => window.clearTimeout(timeoutRef.current);
  }, [resumeAutoDismissal]);

  // Animating the entrance of the toast
  useLayoutEffect(() => {
    setAnimationProps({opacity: 1, transform: 'translateX(0%)'});
  }, [setAnimationProps]);

  return (
    <animated.div
      onTouchStart={stopAutoDismissal}
      onTouchEnd={resumeAutoDismissal}
      onMouseEnter={stopAutoDismissal}
      onMouseLeave={resumeAutoDismissal}
      style={animationProps}
      className={cn({
        'Toast': true,
        'Toast--error': error,
      })}
    >
      <span className="Toast__Content">{content}</span>
      <div className="Toast__Dismiss">
        <Close onClick={onDismissProxy} />
      </div>
    </animated.div>
  )
}

export default memo(Toast);